import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { s25Resource } from "../../../util/s25.resource";
import { Layouts } from "./se.layouts";
import { inject } from "@angular/core";
import { SeLayoutsService } from "./se.layouts.service";
import {
    HttpDeleteRequestOptions,
    HttpGetRequestOptions,
    HttpPutRequestOptions,
} from "../../../dataaccess/data.access";

type State = {
    _configs: Map<number, Layouts.Layout>;
};

export const SeLayoutsStore = signalStore(
    { providedIn: "root" },
    withState(<State>{
        _configs: new Map(),
    }),
    withProps((store) => ({
        _service: inject(SeLayoutsService),
    })),
    withProps((store) => ({
        _listResource: s25Resource({
            loader: async ({ params, abortSignal }) => {
                return await store._service.getLayouts();
            },
        }),
    })),
    withComputed((store) => ({
        list: () => store._listResource.value(),
        defaultLayouts: () => {
            const layouts = store._listResource.value();
            if (!layouts) return undefined;
            return Layouts.newestDefaultPerPage(layouts);
        },
    })),
    withMethods((store) => ({
        getLayout: async (layoutId: number, httpRequestOptions?: HttpGetRequestOptions) => {
            const cached = store._configs().get(layoutId);
            if (cached) return structuredClone(cached);
            const layout = await store._service.getLayout(layoutId, httpRequestOptions);
            patchState(store, {
                _configs: new Map([...store._configs().entries(), [layout.id!, structuredClone(layout)]]),
            });
            return layout;
        },
        updateLayout: async (layout: Layouts.Layout, httpRequestOptions?: HttpPutRequestOptions) => {
            layout.id = await store._service.updateLayout(layout, httpRequestOptions);
            const savedLayout = structuredClone(layout);
            const configs = new Map([...store._configs().entries(), [savedLayout.id!, savedLayout]]);
            const replacedDefaults = Layouts.isPageOverride(savedLayout)
                ? []
                : (store._listResource.value() ?? []).filter(
                      (listed) =>
                          !Layouts.isPageOverride(listed) &&
                          listed.page === savedLayout.page &&
                          listed.id !== savedLayout.id,
                  );
            for (const replaced of replacedDefaults) configs.delete(replaced.id);
            patchState(store, { _configs: configs });
            const replacedIds = new Set(replacedDefaults.map((replaced) => replaced.id));
            store._listResource.resource.update((layouts) => {
                if (!layouts) return;
                const remaining = layouts.filter((listed) => !replacedIds.has(listed.id));
                const savedListItem = <Layouts.LayoutListItem>savedLayout;
                const hasLayout = remaining.some((listed) => listed.id === savedLayout.id);
                if (hasLayout) {
                    return remaining.map((listed) => (listed.id === savedLayout.id ? savedListItem : listed));
                }
                return [...remaining, savedListItem];
            });
        },
        deleteLayout: async (layoutId: number, httpRequestOptions?: HttpDeleteRequestOptions) => {
            await store._service.deleteLayout(layoutId, httpRequestOptions);
            const newConfigsMap = new Map([...store._configs().entries()]);
            newConfigsMap.delete(layoutId);
            patchState(store, { _configs: newConfigsMap });
            store._listResource.resource.update((layouts) => layouts?.filter((l) => l.id !== layoutId));
        },
        isNameTaken: (name: string, layoutId?: number) => {
            return store.list()?.some((l) => l.name === name && l.id !== layoutId);
        },
        getOrgLayouts(orgId: number): Layouts.LayoutListItem[] | undefined {
            const allLayouts = store.list();
            if (!allLayouts) return undefined;
            return allLayouts.filter((layout) => Layouts.sameOrgId(layout.orgId, orgId));
        },
        getOrgCustomLayoutForPage(orgId: number, page: Layouts.Page): Layouts.LayoutListItem | undefined {
            return store
                .list()
                ?.find(
                    (layout) =>
                        Layouts.sameOrgId(layout.orgId, orgId) &&
                        layout.page === page &&
                        Layouts.isPageOverride(layout),
                );
        },
        getOrgLayoutForPage(orgId: number, page: Layouts.Page): Layouts.LayoutListItem | undefined {
            const allLayouts = store.list();
            const found = allLayouts?.find((layout) => Layouts.sameOrgId(layout.orgId, orgId) && layout.page === page);
            if (found) return found;
            return store.defaultLayouts()?.find((layout) => layout.page === page);
        },
        getDefaultLayoutId(page: Layouts.Page): number | undefined {
            const defaults = store.defaultLayouts();
            return defaults?.find((layout) => layout.page === page)?.id;
        },
    })),
);
